// Shared machinery for the notifications contract slice — transcribed, not
// designed. Source of truth: `contracts-legacy/notifications-backend/schemas.ts`
// at the pinned legacy SHA (see CLOSURE.md in that folder for the residual gaps
// this slice INHERITS rather than closes).
//
// Every wart below is load-bearing legacy behavior, kept bug-for-bug per D-013
// rule 1. Do not "fix" a widening, a truthiness check, or an implicit optionality
// without a human-approved parity decision.

import { z } from 'zod';

/** `str(uuid.uuid4())` pins — `contracts-legacy/notifications-backend/schemas.ts:215-216`. */
export const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

/**
 * Job statuses a status handler can REPORT (`schemas.ts:139`). The stored universe
 * is the same four members; a read-time staleness override reports FAILED for a row
 * that stores PENDING/PROCESSING, and `stale: true` is the only disagreement signal.
 */
export const JOB_STATUSES = ['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED'] as const;

/** Terminal statuses a producer stamps on a notification message (`schemas.ts:199`). */
export const NOTIFICATION_STATUSES = ['COMPLETED', 'FAILED'] as const;

/**
 * The writer's `actionType` is deliberately OPEN (`schemas.ts:159-189`): the
 * NotificationsQueue URL is published to SSM for cross-repo producers (marketing,
 * imports — the latter live with `CONTACT_IMPORT`), so pinning the four in-repo
 * literals on the consumer would reject legitimate traffic. The four literals are
 * pinned on each PRODUCER schema instead.
 */
export const NOTIFICATION_ACTION_TYPES_THIS_REPO = [
  'EXPORT',
  'BULK_ACTION',
  'TAX_DOCUMENTS',
  'EMAIL_TAX_DOCUMENTS',
] as const;

/** `entityType` values the four in-repo producers emit (`schemas.ts:192-196`). */
export const NOTIFICATION_ENTITY_TYPES_THIS_REPO = [
  'contacts',
  'subscriptions',
  'payouts',
  'payouts-drilldown',
  'payouts-report',
  'transactions',
  'forms',
  'elements',
  'fundraiser-drilldown',
  'event-drilldown',
  'broadcasts',
  'segments',
  'tax-documents',
  'email-tax-documents',
] as const;

/**
 * `z.unknown()` in Zod 3 is IMPLICITLY OPTIONAL — `z.object({ a: z.unknown() }).strict()`
 * accepts `{}`. D-UNKNOWN-IS-IMPLICITLY-OPTIONAL (`schemas.ts:829-855`). Every key
 * guarded by `presentKey` is an UNCONDITIONAL key of a Python dict LITERAL in the
 * legacy source: the producer can send any JSON value for it but can never OMIT it.
 * `presentKey` restores that absence bound while keeping the value widening intact.
 */
export const presentKey =
  (key: string, sourceCite: string) =>
  (o: unknown): boolean =>
    typeof o === 'object' && o !== null && key in o;

export const presentKeyMessage = (key: string, sourceCite: string) =>
  `${key} is an unconditional key of the metadata dict literal at ${sourceCite}; the source cannot omit it`;

/**
 * Python truthiness, exactly (`schemas.ts:1123-1130`). The legacy consumer guards
 * `clerkOrganizationID` and `actionType` with `if not x: continue` — a truthiness
 * test, not a type test — so `7`, `["x"]`, `{"k": 1}` and `true` all pass while
 * `""`, `0`, `[]`, `{}`, `false` and `null` are skipped.
 */
export const pythonTruthy = (v: unknown): boolean => {
  if (v === null || v === undefined || v === false) return false;
  if (typeof v === 'string') return v.length > 0;
  if (typeof v === 'number') return v !== 0;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v).length > 0;
  return true;
};
